// src/hooks/useElectricityGenYearTotalTWh.js
import { useMemo } from "react";
import { useElectricityGenTS } from "./useElectricityGenTS";

/**
 * Get total generation for a single year in TWh.
 * Works whether totalsByYear is an array (aligned with labels)
 * or an object keyed by year.
 */
export function useElectricityGenYearTotalTWh({
  scenario,
  year,
  unitScale = 0.277778, // pass through to TS hook
} = {}) {
  const { labels, totalsByYear, loading, error } = useElectricityGenTS({
    scenario,
    from: year,
    to: year,
    unitScale,
  });

  const { pickedYear, totalTWh } = useMemo(() => {
    if (!labels?.length || !totalsByYear) {
      return { pickedYear: year, totalTWh: 0 };
    }

    // find exact year in labels
    const idx = labels.findIndex((y) => Number(y) === Number(year));
    const labelAt = (i) => (i >= 0 ? labels[i] : undefined);

    // read total whether array or object
    const readAt = (i) => {
      if (i < 0) return undefined;
      const key = labelAt(i);
      if (Array.isArray(totalsByYear)) return totalsByYear[i];
      return totalsByYear?.[key] ?? totalsByYear?.[String(key)] ?? totalsByYear?.[Number(key)];
    };

    // exact match first; else fall back to the first returned year
    const useIdx = idx >= 0 ? idx : 0;
    const picked = Number(labelAt(useIdx));
    const total = readAt(useIdx) ?? 0;

    return { pickedYear: picked, totalTWh: total };
  }, [labels, totalsByYear, year]);

  return { year: pickedYear, totalTWh, loading, error };
}
